// Customer profile editor.
import { useState } from "react";
import { toast } from "sonner";
import { Save, KeyRound } from "lucide-react";
import { customerApi, useCustomerAuth } from "@/lib/customerAuth";

export default function AccountProfile() {
    const { user, setUser } = useCustomerAuth();
    const [form, setForm] = useState({
        name: user.name || "",
        phone: user.phone || "",
        country: user.country || "",
        address: user.address || "",
        date_of_birth: user.date_of_birth || "",
        preferred_room_type: user.preferred_room_type || "",
        dietary_preferences: user.dietary_preferences || "",
        accessibility_needs: user.accessibility_needs || "",
        special_requests: user.special_requests || "",
        marketing_consent: user.marketing_consent || false,
        newsletter_consent: user.newsletter_consent || false,
    });
    const [busy, setBusy] = useState(false);
    const [pwOpen, setPwOpen] = useState(false);

    const save = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const { data } = await customerApi.patch("/customer/profile", form);
            setUser(data);
            toast.success("Profile updated");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Could not save");
        } finally { setBusy(false); }
    };

    return (
        <div className="space-y-6" data-testid="account-profile">
            <h2 className="font-display text-3xl text-navy">Profile</h2>

            <form onSubmit={save} className="bg-white border border-navy/10 rounded-2xl p-8 space-y-5">
                <div className="grid md:grid-cols-2 gap-4">
                    <F label="Full name" v={form.name} on={(v) => setForm({ ...form, name: v })} testid="prof-name" />
                    <F label="Email" v={user.email} on={() => {}} disabled testid="prof-email" />
                    <F label="Phone" v={form.phone} on={(v) => setForm({ ...form, phone: v })} testid="prof-phone" />
                    <F label="Country" v={form.country} on={(v) => setForm({ ...form, country: v })} testid="prof-country" />
                    <F label="Address" v={form.address} on={(v) => setForm({ ...form, address: v })} testid="prof-address" span2 />
                    <F label="Date of birth" v={form.date_of_birth} on={(v) => setForm({ ...form, date_of_birth: v })} type="date" testid="prof-dob" />
                    <F label="Preferred room type" v={form.preferred_room_type} on={(v) => setForm({ ...form, preferred_room_type: v })} testid="prof-room" />
                </div>

                <div className="pt-2 border-t border-navy/10">
                    <div className="text-[10px] uppercase tracking-[0.22em] text-navy/50 mb-3 mt-4">Preferences (for a smoother stay)</div>
                    <F label="Dietary preferences" v={form.dietary_preferences} on={(v) => setForm({ ...form, dietary_preferences: v })} testid="prof-diet" textarea />
                    <F label="Accessibility needs" v={form.accessibility_needs} on={(v) => setForm({ ...form, accessibility_needs: v })} testid="prof-access" textarea />
                    <F label="Special requests" v={form.special_requests} on={(v) => setForm({ ...form, special_requests: v })} testid="prof-special" textarea />
                </div>

                <div className="pt-2 border-t border-navy/10">
                    <div className="text-[10px] uppercase tracking-[0.22em] text-navy/50 mb-3 mt-4">Marketing preferences</div>
                    <label className="flex items-start gap-3 text-sm text-navy/70 cursor-pointer py-2">
                        <input type="checkbox" checked={form.newsletter_consent} onChange={(e) => setForm({ ...form, newsletter_consent: e.target.checked })} data-testid="prof-newsletter" className="mt-1 accent-navy" />
                        <span>Send me the Serenity Cove newsletter with stays, stories and seasonal offers.</span>
                    </label>
                    <label className="flex items-start gap-3 text-sm text-navy/70 cursor-pointer py-2">
                        <input type="checkbox" checked={form.marketing_consent} onChange={(e) => setForm({ ...form, marketing_consent: e.target.checked })} data-testid="prof-marketing" className="mt-1 accent-navy" />
                        <span>Include me in personalised promotions and loyalty bonuses.</span>
                    </label>
                </div>

                <div className="flex items-center justify-between pt-4 border-t border-navy/10">
                    {user.provider !== "google" && (
                        <button type="button" onClick={() => setPwOpen(true)} data-testid="prof-change-pw" className="text-[11px] uppercase tracking-[0.16em] px-4 py-2 border border-navy/20 rounded-full text-navy hover:bg-navy hover:text-ivory inline-flex items-center gap-2">
                            <KeyRound size={13} /> Change password
                        </button>
                    )}
                    <button type="submit" disabled={busy} data-testid="prof-save" className="ml-auto inline-flex items-center gap-2 bg-navy text-ivory px-6 py-2.5 rounded-full text-[11px] uppercase tracking-[0.22em] hover:bg-navy/90 disabled:opacity-60">
                        <Save size={13} /> {busy ? "Saving…" : "Save profile"}
                    </button>
                </div>
            </form>

            {pwOpen && <PasswordModal onClose={() => setPwOpen(false)} />}
        </div>
    );
}

function F({ label, v, on, type = "text", disabled, textarea, span2, testid }) {
    return (
        <label className={`block ${span2 ? "md:col-span-2" : ""}`}>
            <span className="text-[10px] uppercase tracking-[0.22em] text-navy/60">{label}</span>
            {textarea ? (
                <textarea value={v || ""} onChange={(e) => on(e.target.value)} rows={2} data-testid={testid} disabled={disabled}
                    className={`mt-1.5 w-full border border-navy/15 rounded-lg px-3 py-2 text-sm ${disabled ? "bg-navy/5 text-navy/50" : "focus:border-gold focus:outline-none"}`} />
            ) : (
                <input type={type} value={v || ""} onChange={(e) => on(e.target.value)} data-testid={testid} disabled={disabled}
                    className={`mt-1.5 w-full border border-navy/15 rounded-lg px-3 py-2 text-sm ${disabled ? "bg-navy/5 text-navy/50" : "focus:border-gold focus:outline-none"}`} />
            )}
        </label>
    );
}

function PasswordModal({ onClose }) {
    const [current, setCurrent] = useState("");
    const [next, setNext] = useState("");
    const [busy, setBusy] = useState(false);
    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await customerApi.post("/customer/change-password", { current_password: current, new_password: next });
            toast.success("Password updated");
            onClose();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Could not change password");
        } finally { setBusy(false); }
    };
    return (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="pw-modal">
            <form onSubmit={submit} className="bg-white rounded-2xl w-full max-w-md p-8">
                <h3 className="font-display text-2xl text-navy">Change password</h3>
                <div className="mt-5 space-y-3">
                    <label className="block">
                        <span className="text-[10px] uppercase tracking-[0.22em] text-navy/60">Current password</span>
                        <input type="password" required value={current} onChange={(e) => setCurrent(e.target.value)} data-testid="pw-current" className="mt-1.5 w-full border border-navy/15 rounded-lg px-3 py-2 text-sm" />
                    </label>
                    <label className="block">
                        <span className="text-[10px] uppercase tracking-[0.22em] text-navy/60">New password (min. 8)</span>
                        <input type="password" required minLength={8} value={next} onChange={(e) => setNext(e.target.value)} data-testid="pw-new" className="mt-1.5 w-full border border-navy/15 rounded-lg px-3 py-2 text-sm" />
                    </label>
                </div>
                <div className="mt-6 flex items-center justify-end gap-3">
                    <button type="button" onClick={onClose} className="px-5 py-2.5 text-sm text-navy hover:bg-navy/5 rounded-full">Cancel</button>
                    <button type="submit" disabled={busy} data-testid="pw-save" className="bg-navy text-ivory px-6 py-2.5 rounded-full text-[11px] uppercase tracking-[0.22em] disabled:opacity-60">{busy ? "Saving…" : "Change"}</button>
                </div>
            </form>
        </div>
    );
}
